import type { IconVariant } from "@qendo/theme";
import type { ActivityOptionType, OptionData, SymptomOptionType } from "@qendo/types";
import type { QendoIconSet } from "~/components/QendoIcon";
import { base } from "~/constants/colours";

export enum TrackableType {
    activity = "activity",
    symptom = "symptom",
    journal = "journal",
}

export type MeasurableTrackableType = TrackableType.activity | TrackableType.symptom;
export const MeasurableTrackableTypes: MeasurableTrackableType[] = [TrackableType.activity, TrackableType.symptom];
export type TrackableOption<T extends MeasurableTrackableType> = T extends TrackableType.activity
    ? ActivityOptionType
    : SymptomOptionType;
export type TrackableMeasure<T extends TrackableType = TrackableType> = T extends MeasurableTrackableType
    ? TrackableOption<T>
    : never;

export enum DeviceTrialActivities {
    ibuprofen = "a36",
    paracetamol = "a37",
}

export enum DeviceTrialSymptoms {
    blurred_vision = "s43",
    daytime_sleepiness = "s42",
    difficulty_urinating = "s41",
    food_intolerance = "s39",
    frequent_urination = "s40",
    period_pain = "s38",
    vaginal_irritation = "s44",
    expulsion_of_study_device = "s45",
}

export enum LegacySymptomId {
    flare = "s0",
    pain = "s1",
    mood = "s2",
    acid_reflux = "s24",
    acne = "s16",
    anxiety = "s7",
    bleeding = "s11",
    bloating = "s9",
    bowel_activity = "s14",
    breast_tenderness = "s12",
    clotting = "s29",
    concentration_levels = "s18",
    constipation = "s30",
    cramping = "s13",
    depression = "s8",
    diarrhoea = "s36",
    dizziness = "s31",
    dry_mouth = "s37",
    energy_levels = "s3",
    facial_hair = "s26",
    fatigue = "s19",
    gas = "s27",
    headache = "s4",
    hot_flushes = "s32",
    insomnia = "s33",
    migraine = "s5",
    nausea = "s6",
    night_sweats = "s28",
    ovulation = "s35",
    swelling = "s15",
    stress = "s21",
    temperature = "s20",
    urine = "s23",
    vaginal_discharge = "s17",
    vomiting = "s34",
}

export enum LegacyActivityId {
    sleep = "a0",
    acupuncture = "a26",
    alcohol = "a5",
    bathed = "a15",
    caffeine = "a33",
    chiropractor = "a32",
    cleaning = "a31",
    dry_needling = "a27",
    exams = "a13",
    exercise = "a1",
    family_time = "a18",
    flight = "a8",
    gardening = "a30",
    heat_pack = "a19",
    hospital_visit = "a17",
    ivf = "a29",
    massage = "a25",
    medications = "a3",
    miscarriage = "a11",
    physiotherapy = "a24",
    pregnant = "a9",
    relaxation = "a28",
    sex = "a4",
    shopping = "a35",
    sitting = "a7",
    smoking = "a20",
    social_event = "a6",
    specialist_appointment = "a23",
    study = "a22",
    supplements = "a14",
    surgery = "a12",
    tens_machine = "a34",
    termination = "a10",
    time_off_work = "a16",
    work = "a2",
}

export enum LegacyJournalId {
    food = "F",
    period = "s10",
    journal = "J",
    water = "W",
}

export type LegacyTrackableId = LegacySymptomId | LegacyActivityId | LegacyJournalId;

export type TrackableId = string;

export type TrackableReference = LegacyTrackableId | TrackableId;

export enum Mood {
    very_sad = 1,
    sad = 2,
    neutral = 3,
    happy = 4,
    very_happy = 5,
}

export const MoodKeys: Mood[] = [Mood.very_sad, Mood.sad, Mood.neutral, Mood.happy, Mood.very_happy];

export const MoodMap: Record<Mood, string> = {
    [Mood.very_sad]: "Very sad",
    [Mood.sad]: "Sad",
    [Mood.neutral]: "Neutral",
    [Mood.happy]: "Happy",
    [Mood.very_happy]: "Very happy",
};

export const MoodIconMap: Record<Mood, QendoIconSet> = {
    [Mood.very_sad]: "mood-very-sad",
    [Mood.sad]: "mood-sad",
    [Mood.neutral]: "mood-neutral",
    [Mood.happy]: "mood-happy",
    [Mood.very_happy]: "mood-very-happy",
};

export type MoodColourVariant = Extract<IconVariant, "danger" | "warning" | "success">;
export const MoodColourMap: Record<Mood, MoodColourVariant> = {
    [Mood.very_sad]: "danger",
    [Mood.sad]: "warning",
    [Mood.neutral]: "warning",
    [Mood.happy]: "success",
    [Mood.very_happy]: "success",
};

export const POSITIVE_FEELINGS = [
    "Calm",
    "Confident",
    "Content",
    "Energetic",
    "Excited",
    "Grateful",
    "Inspired",
    "Hopeful",
    "Happy",
    "Loved",
    "Optimistic",
    "Motivated",
    "Proud",
    "Relaxed",
];
export const NEGATIVE_FEELINGS = [
    "Anxious",
    "Disappointed",
    "Depressed",
    "Exhausted",
    "Frustrated",
    "Hopeless",
    "Irritable",
    "Lonely",
    "Overwhelmed",
    "Sad",
    "Unmotivated",
];
export const POSITIVE_MOODS = [Mood.very_happy, Mood.happy];
export const NEGATIVE_MOODS = [Mood.neutral, Mood.sad, Mood.very_sad];

export enum MealType {
    breakfast = 1,
    lunch = 2,
    dinner = 3,
    snack = 4,
}

export const MealTypeMap: Record<MealType, string> = {
    [MealType.breakfast]: "Breakfast",
    [MealType.lunch]: "Lunch",
    [MealType.dinner]: "Dinner",
    [MealType.snack]: "Snack",
};

export enum Cycle {
    started = 1,
    ended = 0,
}

export const OvulationMap = {
    mucus: "Cervical mucus very wet, clear & stretchy",
    basal: "Basal body temperature increased",
    breast: "Breast tenderness",
    headache: "Headache",
};

export const CycleMap: Record<Cycle, string> = {
    [Cycle.started]: "Started",
    [Cycle.ended]: "Ended",
};

export enum SexType {
    protected = 1,
    unprotected = 0,
}
export const SexTypeMap: Record<SexType, string> = {
    [SexType.protected]: "Protected",
    [SexType.unprotected]: "Unprotected",
};

export type BaseTemperature = 35 | 36 | 37 | 38 | 39 | 40 | 41;
export const TemperatureColourMap: Record<BaseTemperature, string> = {
    35: base.blue["500"],
    36: base.green["500"],
    37: base.green["500"],
    38: base.yellow["500"],
    39: base.yellow["500"],
    40: base.red["500"],
    41: base.red["500"],
};

export enum WaterVolume {
    litre = "L",
    millilitre = "mL",
}

export enum ScaleType {
    boolean = "boolean",
    cycle = "cycle",
    meal = "meal",
    water = "water",
    mood = "mood",
    none = "none",
    ovulation = "ovulation",
    sex = "sex",
    sleep = "sleep",
    temperature = "temperature",
    ten = "1-10",
    twenty = "1-20",
    thirty = "1-30",
}

export interface MeasureData<
    T extends TrackableType = TrackableType,
    M extends TrackableMeasure<T> = TrackableMeasure<T>,
> {
    title: string;
    type: M;
    prompt: string;
    required: boolean;
    options?: OptionData[];
}

export interface TrackableData<T extends TrackableType = TrackableType, S extends ScaleType = ScaleType> {
    id: TrackableId;
    title: string;
    icon: QendoIconSet;
    type: T;
    scale: S;
    scale_name: string;
    measures?: MeasureData<T>[];
    legacy_id: TrackableReference | null;
}

export type ActivityData<S extends ScaleType = ScaleType> = TrackableData<TrackableType.activity, S>;
export type SymptomData<S extends ScaleType = ScaleType> = TrackableData<TrackableType.symptom, S>;
export type JournalTrackableData<S extends ScaleType = ScaleType> = TrackableData<TrackableType.journal, S>;

export type OtherableTrackableType = TrackableType.activity | TrackableType.symptom;
export type TrackableList = ActivityData[] | SymptomData[] | JournalTrackableData[];
export type OtherableTrackableList = ActivityData[] | SymptomData[];

type TrackableRecord<T extends TrackableType> = Record<T, TrackableData<T>[]>;

export type TrackableStore = {
    [TrackableType.activity]: ActivityData[];
    [TrackableType.symptom]: SymptomData[];
    [TrackableType.journal]: JournalTrackableData[];
} & TrackableRecord<TrackableType>;

export type OtherableTrackableStore = {
    [TrackableType.activity]: ActivityData[];
    [TrackableType.symptom]: SymptomData[];
} & TrackableRecord<OtherableTrackableType>;
